export const WORK_HISTORY_MIN_YEARS = Number(
  process.env.REACT_APP_WORK_HISTORY_MIN_YEARS ?? 10,
);

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const toMonthIndex = (dateValue) => {
  if (!dateValue) return null;
  if (typeof dateValue === "string") {
    const match = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return Number(match[1]) * 12 + Number(match[2]) - 1;
    }
  }
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  return date.getFullYear() * 12 + date.getMonth();
};

const fromMonthIndex = (monthIndex) => ({
  year: Math.floor(monthIndex / 12),
  month: monthIndex % 12,
});

const formatDateInputFromMonth = (monthIndex) => {
  const { year, month } = fromMonthIndex(monthIndex);
  return `${year}-${String(month + 1).padStart(2, "0")}-01`;
};

export const formatMonth = (monthIndex) => {
  const { year, month } = fromMonthIndex(monthIndex);
  return `${MONTH_NAMES[month]} ${year}`;
};

const formatMonthRange = (start, end) =>
  start === end ? formatMonth(start) : `${formatMonth(start)} - ${formatMonth(end)}`;

export const getWorkHistoryCoverageWindow = (
  requiredYears = WORK_HISTORY_MIN_YEARS,
  asOfDate = new Date(),
) => {
  const years = Number(requiredYears);
  const currentMonth = toMonthIndex(asOfDate) ?? toMonthIndex(new Date());

  if (!Number.isFinite(years) || years <= 0) {
    return { requiredYears: 0, startMonth: null, endMonth: currentMonth };
  }

  return {
    requiredYears: years,
    startMonth: currentMonth - years * 12,
    endMonth: currentMonth,
  };
};

export const sortWorkHistoriesByDate = (entries = []) =>
  [...entries].sort((a, b) => {
    const aMonth = toMonthIndex(a?.wh_datefrom) ?? Number.MAX_SAFE_INTEGER;
    const bMonth = toMonthIndex(b?.wh_datefrom) ?? Number.MAX_SAFE_INTEGER;
    return aMonth - bMonth;
  });

export const validateWorkHistoryCoverage = (
  entries = [],
  requiredYears = WORK_HISTORY_MIN_YEARS,
  asOfDate = null,
) => {
  const window = getWorkHistoryCoverageWindow(requiredYears, asOfDate || new Date());

  if (window.requiredYears <= 0) {
    return { isValid: true, missingRanges: [], message: "", ...window };
  }

  let coveredThrough = window.startMonth;
  const missingRanges = [];

  const ranges = sortWorkHistoriesByDate(entries)
    .map((entry) => {
      const start = toMonthIndex(entry?.wh_datefrom);
      const end = toMonthIndex(entry && entry.wh_dateto);
      const endExclusive =
        entry?.wh_status === "Yes"
          ? window.endMonth + 1
          : (end == null ? null : end + 1);

      if (start == null || endExclusive == null) return null;

      return {
        start: Math.max(start, window.startMonth),
        endExclusive: Math.min(endExclusive, window.endMonth + 1),
      };
    })
    .filter(
      (range) =>
        range &&
        range.endExclusive > window.startMonth &&
        range.start <= window.endMonth,
    );

  ranges.forEach((range) => {
    if (range.endExclusive <= coveredThrough) return;

    if (range.start > coveredThrough) {
      missingRanges.push({
        start: coveredThrough,
        end: Math.min(range.start - 1, window.endMonth),
      });
    }

    coveredThrough = Math.max(coveredThrough, range.endExclusive);
  });

  if (coveredThrough <= window.endMonth) {
    missingRanges.push({ start: coveredThrough, end: window.endMonth });
  }

  const formattedMissingRanges = missingRanges.map((range) =>
    formatMonthRange(range.start, range.end),
  );
  const message = formattedMissingRanges.length
    ? `Please add history for the missing month(s): ${formattedMissingRanges.join(", ")}.`
    : "";

  return {
    isValid: formattedMissingRanges.length === 0,
    missingRanges: formattedMissingRanges,
    missingMonthRanges: missingRanges,
    message,
    ...window,
  };
};

export const getFirstMissingWorkHistoryDateRange = (
  entries = [],
  requiredYears = WORK_HISTORY_MIN_YEARS,
  asOfDate = null,
) => {
  const coverage = validateWorkHistoryCoverage(entries, requiredYears, asOfDate);
  const firstMissingRange = coverage.missingMonthRanges?.[0];

  if (!firstMissingRange) return null;

  return {
    from: formatDateInputFromMonth(firstMissingRange.start),
    to: formatDateInputFromMonth(firstMissingRange.end),
  };
};

export const getWorkHistoryRequirementText = (
  requiredYears = WORK_HISTORY_MIN_YEARS,
  asOfDate = null,
) => {
  const window = getWorkHistoryCoverageWindow(requiredYears, asOfDate || new Date());
  if (window.requiredYears <= 0) return "";

  return `Please include at least ${window.requiredYears} years of continuous history, from ${formatMonth(
    window.startMonth,
  )} through ${formatMonth(window.endMonth)}, with no missing months.`;
};
